import { types } from 'mobx-state-tree';
import { Node } from './node-model';

const Animation = types.model('Animation', {
  delay: types.optional(types.number, 0),
  duration: types.optional(types.number, 1),
  enabled: types.optional(types.boolean, false),
  type: types.optional(types.string, ''),
  name: types.optional(types.string, ''),
  data: types.optional(types.frozen(), {}),
});

const ShapeFilter = types.model('ShapeFilter', {
  intensity: types.optional(types.number, 1),
});

export const Shape = Node.named('Shape')
  .props({
    x: types.optional(types.number, 0),
    y: types.optional(types.number, 0),
    width: types.optional(types.number, 100),
    height: types.optional(types.number, 100),
    rotation: types.optional(types.number, 0),

    animations: types.optional(types.array(Animation), []),

    // Effects
    blurEnabled: types.optional(types.boolean, false),
    blurRadius: types.optional(types.number, 10),
    brightnessEnabled: types.optional(types.boolean, false),
    brightness: types.optional(types.number, 0),
    sepiaEnabled: types.optional(types.boolean, false),
    grayscaleEnabled: types.optional(types.boolean, false),
    warmEnabled: types.optional(types.boolean, false),
    coldEnabled: types.optional(types.boolean, false),
    filters: types.optional(types.map(ShapeFilter), {}),

    // Shadow
    shadowEnabled: types.optional(types.boolean, false),
    shadowBlur: types.optional(types.number, 5),
    shadowOffsetX: types.optional(types.number, 0),
    shadowOffsetY: types.optional(types.number, 0),
    shadowColor: types.optional(types.string, 'black'),
    shadowOpacity: types.optional(types.number, 1),

    // Interaction flags
    draggable: types.optional(types.boolean, true),
    resizable: types.optional(types.boolean, true),
    contentEditable: types.optional(types.boolean, true),
    styleEditable: types.optional(types.boolean, true),
  })
  .preProcessSnapshot((snapshot) => {
    if (!snapshot) return snapshot;
    const s = { ...snapshot };

    // Coerce numeric properties that may arrive as strings from templates
    const numericKeys = [
      'x', 'y', 'width', 'height', 'rotation',
      'blurRadius', 'brightness',
      'shadowBlur', 'shadowOffsetX', 'shadowOffsetY', 'shadowOpacity',
    ];
    numericKeys.forEach((key) => {
      if (key in s && typeof s[key] === 'string') {
        s[key] = parseFloat(s[key]) || 0;
      }
    });

    // Coerce boolean properties that may arrive as strings
    const boolKeys = [
      'blurEnabled', 'brightnessEnabled', 'sepiaEnabled', 'grayscaleEnabled', 'warmEnabled', 'coldEnabled',
      'shadowEnabled', 'draggable', 'resizable', 'contentEditable', 'styleEditable',
    ];
    boolKeys.forEach((key) => {
      if (key in s && typeof s[key] === 'string') {
        s[key] = s[key] === 'true';
      }
    });

    return s;
  })
  .actions((self) => ({
    setAnimation(type, attrs) {
      const existing = self.animations.find((a) => a.type === type);
      if (existing) {
        Object.assign(existing, attrs);
      } else {
        self.animations.push({ type, ...attrs });
      }
    },
    setFilter(type, value) {
      self.filters.set(type, { intensity: value });
    },
  }));

export { Animation, ShapeFilter };
